import { HelpCircle, LogOut, Plus, UserCircle } from 'lucide-react';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@doger/ui/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from '@doger/ui/components/ui/sidebar';
import { useJWT, useSignOut, useUser } from '@doger/core/modules/auth';
import { Logo } from '@doger/core/shared/ui/logo';

import { useAnalytics } from '@doger/chat/shared/analytics';

import { useAuth } from '../../modules/auth/auth.context';
import { ChatList } from '../../modules/chat';
import { useTranslation } from '../../shared/i18n';
import { SupportModal } from '../support-modal';
import styles from './sidebar.module.scss';

export const AppSidebar = memo(() => (
  <Sidebar>
    <AppSidebarHeader />

    <AppSidebarBody />

    <AppSidebarFooter />
  </Sidebar>
));

const AppSidebarHeader = memo(() => {
  const jwt = useJWT();
  const { setIsAuthModalOpen } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setOpenMobile } = useSidebar();

  return (
    <SidebarHeader className={styles.sidebarLogo}>
      <AppSidebarLogo />

      <Button
        className={styles.newChatButton}
        variant='outlined'
        size='lg'
        palette='ghost'
        title={t('sidebar.newChat')}
        onClick={() => {
          if (jwt) {
            navigate('/');
          } else {
            setIsAuthModalOpen(true);
          }
          setOpenMobile(false);
        }}
      >
        <Plus className={styles.newChatButton__icon} />
        <span className={styles.newChatButton__text}>
          {t('sidebar.newChat')}
        </span>
      </Button>
    </SidebarHeader>
  );
});

const AppSidebarBody = memo(() => {
  const jwt = useJWT();
  const { setOpenMobile } = useSidebar();

  const handleChatClick = useCallback(() => {
    setOpenMobile(false);
  }, [setOpenMobile]);

  if (!jwt) {
    return (
      <SidebarContent className={styles.chatList}>
        <div className={styles.chatList__container} />
      </SidebarContent>
    );
  }

  return (
    <SidebarContent>
      <ChatList onChatClick={handleChatClick} />
    </SidebarContent>
  );
});

const AppSidebarFooter = memo(() => {
  const jwt = useJWT();
  const { setIsAuthModalOpen, setIsUserProfileDetailsModalOpen } = useAuth();
  const { t } = useTranslation();
  const signOut = useSignOut();
  const { data: user } = useUser();
  const analytics = useAnalytics();

  const getUserDisplayName = () => {
    if (!!user?.firstName && !!user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    return t('auth.userProfile.defaultName', { ns: 'core' });
  };

  if (!jwt) {
    return (
      <SidebarFooter className={styles.footer}>
        <Button
          onClick={() => setIsAuthModalOpen(true)}
          variant='contained'
          size='md'
          palette='primary'
        >
          {t('auth.signIn')}
        </Button>
      </SidebarFooter>
    );
  }

  return (
    <SidebarFooter className={styles.footer}>
      <SupportModal>
        <Button
          className={styles.footerButton}
          variant='contained'
          size='lg'
          palette='ghost'
        >
          <HelpCircle />
          <span className={styles.footerButtonName}>
            {t('support.modal.trigger')}
          </span>
        </Button>
      </SupportModal>

      <Button
        className={styles.footerButton}
        variant='contained'
        size='lg'
        palette='ghost'
        onClick={() => setIsUserProfileDetailsModalOpen(true)}
      >
        <UserCircle />

        <span className={styles.footerButtonProfile}>
          <span className={styles.footerButtonName}>
            {getUserDisplayName()}
          </span>

          <span className={styles.footerButtonEmail}>{jwt.email}</span>
        </span>
      </Button>

      <Button
        className={styles.footerButton}
        onClick={() => signOut.mutate()}
        variant='contained'
        size='lg'
        palette='ghost'
      >
        <LogOut />
        {t('auth.userProfile.signOut', { ns: 'core' })}
      </Button>
    </SidebarFooter>
  );
});

const AppSidebarLogo = memo(() => {
  const { t } = useTranslation('app');

  return (
    <div className={styles.sidebarLogo__header}>
      <div className={styles.sidebarLogo__icon}>
        <Logo width={55} height={55} style={{ marginBottom: -5 }} />
      </div>
      <div className={styles.sidebarLogo__text}>
        <h1 className={styles.sidebarLogo__title}>{t('branding.name')}</h1>
        <p className={styles.sidebarLogo__subtitle}>{t('branding.slogan')}</p>
      </div>
    </div>
  );
});
